/**
 * @name IntegrationLoginPage
 * @description Página para autenticação transparente via integração externa
 * @author Gabriel M. Guimarães | gabrielmg7
 * @package src/components/pages/auth/IntegrationLoginPage.tsx
 */

import { Alert, Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useThemeContext from '../../../../themes/hooks/useThemeContext';

interface IntegrationLoginPageProps {
     anoLetivo?: string;
     matricula?: string;
     // 🔧 Recebe dados do hook via props para evitar múltiplas instâncias
     isProcessingIntegration?: boolean;
     integrationError?: string | null;
     errorType?: 'RESPONSAVEL_SEM_EMAIL' | 'DATABASE_ERROR' | 'GENERIC_ERROR' | null;
     responsavelData?: any | null;
     resetProcessingState?: () => void;
}

/**
 * Componente para processar autenticação via integração externa
 *
 * Casos de uso:
 * 1. URL com parâmetros: ?ano_letivo=2025&matricula=7227
 * 2. Componente direto com props
 * 3. Página de fallback para integração
 */
const IntegrationLoginPage: React.FC<IntegrationLoginPageProps> = ({ anoLetivo: propAnoLetivo, matricula: propMatricula, isProcessingIntegration = false, integrationError = null, errorType = null, responsavelData = null, resetProcessingState }) => {
     const { theme } = useThemeContext();
     const navigate = useNavigate();

     const [urlParams, setUrlParams] = useState<{ anoLetivo?: string; matricula?: string }>({});
     // const [hasAttemptedAuth, setHasAttemptedAuth] = useState(false); // Removido - não utilizado após refatoração
     const [loadingTimeout, setLoadingTimeout] = useState(false); // Detecta timeout de loading

     // Extrai parâmetros da URL
     useEffect(() => {
          const searchParams = new URLSearchParams(window.location.search);
          const anoLetivoUrl = searchParams.get('ano_letivo');
          const matriculaUrl = searchParams.get('matricula');
          const errorParam = searchParams.get('error');

          setUrlParams({
               anoLetivo: anoLetivoUrl || undefined,
               matricula: matriculaUrl || undefined,
          });

          // Se há parâmetro de erro, não tenta autenticação automática
          if (errorParam === 'true') {
               console.log('🚫 [IntegrationLoginPage] Parâmetro de erro detectado - não tenta autenticação');
          }
     }, []);

     // 🔧 REMOVIDO: Execução de autenticação automática - agora é responsabilidade apenas do middleware

     // Retry da autenticação
     const handleRetry = () => {
          if (resetProcessingState) {
               resetProcessingState(); // Reseta todos os controles
          }
          setLoadingTimeout(false); // Reseta timeout
     };

     // Detecta loading infinito
     useEffect(() => {
          if (isProcessingIntegration) {
               const timeoutId = setTimeout(() => {
                    console.warn('⚠️ Loading há mais de 20 segundos, oferecendo alternativas');
                    setLoadingTimeout(true);
               }, 20000);

               return () => clearTimeout(timeoutId);
          } else {
               setLoadingTimeout(false);
          }
     }, [isProcessingIntegration]);

     // Redireciona para login manual
     const handleManualLogin = () => {
          navigate('/auth/login', { replace: true });
     };

     // Renderiza tela específica para responsável sem email
     const renderResponsavelSemEmailError = () => (
          <Box textAlign='center'>
               <Typography
                    variant='h5'
                    color='warning.main'
                    gutterBottom
               >
                    📧 E-mail Não Cadastrado
               </Typography>

               <Typography
                    variant='body1'
                    sx={{ mb: 3, color: 'text.secondary' }}
               >
                    Para utilizar o sistema IS Cantina é necessário ter um e-mail cadastrado.
               </Typography>

               <Box
                    sx={{
                         bgcolor: 'warning.light',
                         p: 3,
                         borderRadius: 2,
                         mb: 3,
                         textAlign: 'left',
                    }}
               >
                    <Typography
                         variant='h6'
                         gutterBottom
                    >
                         📋 Dados Encontrados:
                    </Typography>
                    <Typography
                         variant='body2'
                         sx={{ mb: 1 }}
                    >
                         <strong>Nome:</strong> {responsavelData?.nome}
                    </Typography>
                    <Typography
                         variant='body2'
                         sx={{ mb: 1 }}
                    >
                         <strong>CPF:</strong> {responsavelData?.cpf}
                    </Typography>
                    {responsavelData?.aluno && (
                         <>
                              <Typography
                                   variant='body2'
                                   sx={{ mb: 1 }}
                              >
                                   <strong>Matrícula:</strong> {responsavelData.aluno.matricula}
                              </Typography>
                              <Typography variant='body2'>
                                   <strong>Ano Letivo:</strong> {responsavelData.aluno.ano_letivo}
                              </Typography>
                         </>
                    )}
               </Box>

               <Box
                    sx={{
                         bgcolor: 'info.light',
                         p: 3,
                         borderRadius: 2,
                         mb: 3,
                         textAlign: 'left',
                    }}
               >
                    <Typography
                         variant='h6'
                         gutterBottom
                         color='info.dark'
                    >
                         💳 Dados Necessários para Recargas de Crédito:
                    </Typography>
                    <Typography
                         variant='body2'
                         sx={{ mb: 2 }}
                    >
                         Para comprar recargas de crédito no sistema, precisamos dos seguintes dados básicos:
                    </Typography>
                    <Box
                         component='ul'
                         sx={{ pl: 2, m: 0 }}
                    >
                         <Typography
                              component='li'
                              variant='body2'
                              sx={{ mb: 0.5 }}
                         >
                              📧 <strong>E-mail válido</strong> (para notificações e confirmações)
                         </Typography>
                         <Typography
                              component='li'
                              variant='body2'
                              sx={{ mb: 0.5 }}
                         >
                              📱 <strong>Telefone</strong> (para contato de emergência)
                         </Typography>
                         <Typography
                              component='li'
                              variant='body2'
                         >
                              🏠 <strong>Endereço completo</strong> (para questões fiscais)
                         </Typography>
                    </Box>
               </Box>

               <Alert
                    severity='error'
                    sx={{ mb: 3, textAlign: 'left' }}
               >
                    <Typography
                         variant='subtitle2'
                         gutterBottom
                    >
                         🚫 Acesso Negado:
                    </Typography>
                    <Typography variant='body2'>
                         Não é possível criar uma conta pois o responsável financeiro não possui e-mail cadastrado. Entre em contato com o <strong>setor acadêmico da sua instituição</strong> para atualizar seu cadastro com um e-mail válido.
                    </Typography>
               </Alert>

               <Box
                    display='flex'
                    gap={2}
                    justifyContent='center'
               >
                    <Button
                         variant='outlined'
                         onClick={handleRetry}
                    >
                         Tentar Novamente
                    </Button>
               </Box>
          </Box>
     );

     // Renderiza tela de erro de banco de dados
     const renderDatabaseError = () => (
          <Box textAlign='center'>
               <Typography
                    variant='h5'
                    color='error.main'
                    gutterBottom
               >
                    🛠️ Erro do Sistema
               </Typography>

               <Typography
                    variant='body1'
                    sx={{ mb: 3, color: 'text.secondary' }}
               >
                    Ocorreu um problema interno no sistema.
               </Typography>

               <Alert
                    severity='error'
                    sx={{ mb: 3, textAlign: 'left' }}
               >
                    <Typography variant='body2'>{integrationError}</Typography>
               </Alert>

               <Box
                    display='flex'
                    gap={2}
                    justifyContent='center'
               >
                    <Button
                         variant='outlined'
                         onClick={handleRetry}
                    >
                         Tentar Novamente
                    </Button>
                    <Button
                         variant='contained'
                         onClick={handleManualLogin}
                    >
                         Login Manual
                    </Button>
               </Box>
          </Box>
     );

     // Renderiza tela de erro genérico
     const renderGenericError = () => (
          <Box textAlign='center'>
               <Typography
                    variant='h5'
                    color='error.main'
                    gutterBottom
               >
                    ❌ Erro na Autenticação
               </Typography>

               <Typography
                    variant='body1'
                    sx={{ mb: 3, color: 'text.secondary' }}
               >
                    Não foi possível processar sua autenticação.
               </Typography>

               <Alert
                    severity='error'
                    sx={{ mb: 3, textAlign: 'left' }}
               >
                    <Typography variant='body2'>{integrationError}</Typography>
               </Alert>

               <Box
                    display='flex'
                    gap={2}
                    justifyContent='center'
               >
                    <Button
                         variant='outlined'
                         onClick={handleRetry}
                    >
                         Tentar Novamente
                    </Button>
                    <Button
                         variant='contained'
                         onClick={handleManualLogin}
                    >
                         Login Manual
                    </Button>
               </Box>
          </Box>
     );

     const anoLetivo = propAnoLetivo || urlParams.anoLetivo;
     const matricula = propMatricula || urlParams.matricula;

     return (
          <Box
               display='flex'
               justifyContent='center'
               alignItems='center'
               minHeight='100vh'
               bgcolor={theme.palette.background.default}
               p={2}
          >
               <Card sx={{ maxWidth: 500, width: '100%' }}>
                    <CardContent sx={{ textAlign: 'center', p: 4 }}>
                         {/* Título */}
                         <Typography
                              variant='h4'
                              component='h1'
                              gutterBottom
                              color='primary'
                         >
                              IS Cantina
                         </Typography>

                         {/* Estado de carregamento */}
                         {isProcessingIntegration && (
                              <>
                                   <CircularProgress
                                        size={60}
                                        sx={{ mb: 3 }}
                                   />
                                   <Typography
                                        variant='h6'
                                        gutterBottom
                                   >
                                        Processando autenticação...
                                   </Typography>
                                   <Typography
                                        variant='body2'
                                        color='text.secondary'
                                        sx={{ mb: loadingTimeout ? 2 : 0 }}
                                   >
                                        Conectando com o sistema acadêmico
                                   </Typography>

                                   {/* Botão de escape para loading prolongado */}
                                   {loadingTimeout && (
                                        <>
                                             <Alert
                                                  severity='warning'
                                                  sx={{ mt: 2, mb: 2, textAlign: 'left' }}
                                             >
                                                  <Typography variant='body2'>O processamento está demorando mais que o esperado.</Typography>
                                             </Alert>
                                             <Box
                                                  display='flex'
                                                  gap={2}
                                                  justifyContent='center'
                                             >
                                                  <Button
                                                       variant='outlined'
                                                       onClick={handleRetry}
                                                       size='small'
                                                  >
                                                       Cancelar e Tentar Novamente
                                                  </Button>
                                                  <Button
                                                       variant='contained'
                                                       onClick={handleManualLogin}
                                                       size='small'
                                                  >
                                                       Ir para Login Manual
                                                  </Button>
                                             </Box>
                                        </>
                                   )}
                              </>
                         )}

                         {/* Estados de erro com telas dedicadas */}
                         {integrationError && !isProcessingIntegration && (
                              <>
                                   {errorType === 'RESPONSAVEL_SEM_EMAIL' && renderResponsavelSemEmailError()}
                                   {errorType === 'DATABASE_ERROR' && renderDatabaseError()}
                                   {errorType === 'GENERIC_ERROR' && renderGenericError()}
                              </>
                         )}


                         {/* Informações de debug (apenas em desenvolvimento) */}
                         {process.env.NODE_ENV === 'development' && (anoLetivo || matricula) && (
                              <Box
                                   mt={4}
                                   p={2}
                                   bgcolor='grey.100'
                                   borderRadius={1}
                              >
                                   <Typography
                                        variant='caption'
                                        display='block'
                                        gutterBottom
                                   >
                                        Parâmetros de Integração:
                                   </Typography>
                                   <Typography
                                        variant='body2'
                                        fontFamily='monospace'
                                   >
                                        Ano Letivo: {anoLetivo || 'N/A'}
                                   </Typography>
                                   <Typography
                                        variant='body2'
                                        fontFamily='monospace'
                                   >
                                        Matrícula: {matricula || 'N/A'}
                                   </Typography>
                              </Box>
                         )}
                    </CardContent>
               </Card>
          </Box>
     );
};

export default IntegrationLoginPage;
